import { useEffect, useMemo, useRef, useState } from "react";
import { BodyText, Box, BoxContent, BoxRow, CheckMark, ContentHeadline, ContentLabel, ContentSubtext, CustomButton, Feature, FeatureList, FeatureListIcon, FeatureText, Headline, Price, PriceContainer, ProgressBar, ProgressStep, SmallText, Valuta, HeadingContainer, ProductBannerContainer, ContactMeContainer, CloseButton, CloseIcon, ModalInputContainer, ModalInput, ErrorMessage, Button, ContactMeHeadline, ContactMeSubtitle, ContactMeChosenProduct, ContactMeContent } from "./ProductBanner.style";
import ProductModal, { ProductModalOption } from "./Components/ProductModal";
import { BloomreachLeadModel } from "../../types/BloomreachLeadModal";
import { postBloomreachLead } from "../../utils/api";
import Analytics from "../../utils/analytics";
import { TrackingFlowTypeEnum } from "../../types/TrackingFlowTypeEnum";

interface ProductBoxData {
  label?: string;
  headline: string;
  price: string;
  subtext: string;
  offer: boolean;
  buttonHref: string;
  buttonText: string;
}

export interface ProductFeature {
  FeatureDescription: string;
  FeatureTitle: string;
  Key: string;
}

export interface ProductConfig {
  ProductTitle: string;
  Price: string;
  ProductBannerText: string;
  Features: ProductFeature[];
  ProductTargetVariant: string;
  Key: string;
  ProductId: string;
  DefaultChoosen: boolean;
  ProductSubText: string;
  ProductCardCta: string;
  ProductCardSmallText: string;
}

export default function ProductBanner() {
    const rowRef = useRef<HTMLDivElement>(null);
    const boxRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [productList, setProductList] = useState<ProductConfig[]>([]);
    const [headline, setHeadline] = useState<string>();
    const [subtitle, setSubtitle] = useState<string>();
    const [packageText, setPackageText] = useState<string>();
    const [campaignSource, setCampaignSource] = useState<string>();
    const [btnUrl, setBtnUrl] = useState<string>();
    const [activeStep, setActiveStep] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalHref, setModalHref] = useState<string>();
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState<ProductModalOption | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductConfig | null>(null);
    const [firstName, setFirstName] = useState<string>();
    const [mobile, setMobile] = useState<string>();
    const [showMobileError, setShowMobileError] = useState<boolean>(false);
    const [showFirstNameError, setShowFirstNameError] = useState<boolean>(false);

    //Get data from umbraco backend
    useEffect(() => {
        const form = document.querySelector(`[data-component='product-banner']`) as HTMLElement;
        //console.log(form?.dataset); 
        const headline = form?.dataset.productcardsHeadline;
        const subtitle = form?.dataset.productcardsSubtitle;
        const products = form?.dataset.productcardsProductlist;
        const btnUrl = form?.dataset.productcardsBtnUrl;
        const packageText = form?.dataset.productcardsPackageText;
        const campaignSource = form?.dataset.productcardsCampaign;
        setHeadline(headline)
        setSubtitle(subtitle)
        setBtnUrl(btnUrl)
        setPackageText(packageText)
        setCampaignSource(campaignSource)
        if(products) {
           setProductList(JSON.parse(products) as ProductConfig[]);
        }
    }, [])

    const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, product: ProductConfig) => {
        e.preventDefault();
        setModalHref(href);
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    //Swipe on mobile
    useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const centerMiddleBox = () => {
        const defaultIndex = productList.findIndex((box) => box.DefaultChoosen);
        const targetIndex = defaultIndex !== -1 ? defaultIndex : Math.floor(productList.length / 2);
        const middle = boxRefs.current[targetIndex];
        if (!middle) return;

        const isMobile = window.matchMedia("(max-width: 700px)").matches;
        if (!isMobile) return;

        const target = middle.offsetLeft - row.clientWidth / 2 + middle.clientWidth / 2;
        row.scrollTo({ left: target, behavior: "auto" });
    };

    const updateActiveStep = () => {
        const rowCenter = row.scrollLeft + row.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        boxRefs.current.forEach((box, index) => {
        if (!box) return;
        const boxCenter = box.offsetLeft + box.clientWidth / 2;
        const distance = Math.abs(boxCenter - rowCenter);
        if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
        }
        });

        setActiveStep(closestIndex);
    };

    const init = () => {
        centerMiddleBox();
        updateActiveStep();
    };

    init();
    window.addEventListener("resize", init);

    let ticking = false;
    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
        updateActiveStep();
        ticking = false;
        });
    };
    row.addEventListener("scroll", onScroll);

    return () => {
        window.removeEventListener("resize", init);
        row.removeEventListener("scroll", onScroll);
    };
    }, [productList]);



    //Lead modal
    const resetContactFields = () => {
        setFirstName("");
        setMobile("");
        setShowFirstNameError(false);
        setShowMobileError(false);
        setSelectedProduct(null);
    };

     const handleLeadOptionSelected = (option: ProductModalOption) => {
        setSelectedOption(option);
        setIsModalOpen(false);
        setShowLeadModal(true);
    };

    const customerTranslation = useMemo(() => {
        if (!selectedOption) return "";
        switch (selectedOption.occupation) {
            case "selfemployed":
                return "selvstændig";
            case "employee":
                return "ansat";
            case "unemployed":
                return "ledig";
            case "student":
                return "studerende";
            default:
                return "";
        }
    }, [selectedOption]); 

    //Api send contact me
    const onSend = async () => {
        const chosenProductText = selectedProduct?.ProductTitle ?? "";
        let lead = {} as BloomreachLeadModel;
        lead.firstName = firstName as string
        lead.phone = mobile as string
        lead.campaign = "Ring mig op - produktslider"
        lead.source = `${campaignSource}`
        lead.occupation = customerTranslation as string
        lead.comment = `${chosenProductText}`

        await postBloomreachLead({
            ...lead
            }).then(() => {
                setShowLeadModal(false);
                Analytics.getInstance().trackLeadFromProductCard(TrackingFlowTypeEnum.ProductSlider);
            });
      } 

    const isMobileValid = (value?: string) => {
        if (!value) return false;
        const digitsOnly = value.replace(/[^0-9+]/g, "");
        const withoutCountryCode = digitsOnly.replace(/^(\+45|0045)/, "");
        return /^[0-9]{8}$/.test(withoutCountryCode);
    };

    const isContactFormValid = useMemo(() => {
      return !!(firstName && firstName.trim() !== "") && isMobileValid(mobile);
    }, [firstName, mobile]);

    const checkSubmit = () => {
      if ((firstName && firstName.trim() !== "") && isMobileValid(mobile)) {
          onSend()
          setFirstName("");
          setMobile("");
          setShowMobileError(false)
          setShowFirstNameError(false)
      } else {
          if (!firstName || firstName.trim() === "") {
              setShowFirstNameError(true);
          }
          if (!isMobileValid(mobile)) {
              setShowMobileError(true);
          }
      }
    };

  return (
    <ProductBannerContainer>
      <HeadingContainer className="container">
        {headline != "" && headline != undefined && (
            <Headline>{headline}</Headline>
        )}
        {subtitle != "" && subtitle != undefined && (
          <BodyText dangerouslySetInnerHTML={{__html: subtitle}} />
        )}
      </HeadingContainer>
      <BoxRow ref={rowRef} className="container">
        {productList.map((box, index) => (
          <Box key={box.Key} ref={(el) => (boxRefs.current[index] = el)} offer={box.ProductBannerText && box.ProductBannerText != "" && box.DefaultChoosen ? true : false}>
            <BoxContent>
                {box.ProductBannerText && box.ProductBannerText != "" && box.DefaultChoosen && ( 
                    <ContentLabel dangerouslySetInnerHTML={{__html: box.ProductBannerText}} />
                )}
                <ContentHeadline>{box.ProductTitle}</ContentHeadline>
                <PriceContainer>
                    <Price offer={box.ProductBannerText && box.ProductBannerText != "" && box.DefaultChoosen ? true : false}>
                        {box.Price} <Valuta>kr./md.</Valuta>
                    </Price>
                </PriceContainer>
                <ContentSubtext>{box.ProductSubText}</ContentSubtext>
                <FeatureList>
                    {box.Features.map(feature =>
                        <Feature key={feature.Key}>
                            <FeatureListIcon offer={box.ProductBannerText && box.ProductBannerText != "" && box.DefaultChoosen ? true : false}>
                                <CheckMark offer={box.ProductBannerText && box.ProductBannerText != "" && box.DefaultChoosen ? true : false} />
                            </FeatureListIcon>
                            <FeatureText>{feature.FeatureTitle}</FeatureText>
                        </Feature>
                    )}
                </FeatureList>
                <CustomButton
                    onClick={(e) => handleButtonClick(e, `${btnUrl}?produkt=${box.ProductTargetVariant}&campaign=${campaignSource}&step=3`, box)}
                    offer={box.ProductBannerText && box.ProductBannerText != "" ? true : false}
                >
                    {box.ProductCardCta != "" && box.ProductCardCta != undefined ? box.ProductCardCta : "Køb"}
                </CustomButton>
                <SmallText>{box.ProductCardSmallText}</SmallText>
            </BoxContent>
          </Box>
        ))}
      </BoxRow>
      <ProgressBar>
        {productList.map((box, index) => (
          <ProgressStep key={box.Key} $active={index === activeStep} />
        ))}
      </ProgressBar>
      {/* Occupation modal for selecting the user's current work situation */}
      {isModalOpen &&
        <ProductModal href={modalHref} onClose={() => setIsModalOpen(false)} onOptionSelect={handleLeadOptionSelected} title="Hvor er du i dit arbejdsliv lige nu?" subtitle="Vi tilpasser produkter, priser og dækning efter dit valg." options={[{ title: "Lønmodtager - i job", occupation: "employee" }, { title: "Lønmodtager - ledig", occupation: "unemployed" }, { title: "Studerende", occupation: "student" }, { title: "Selvstændig", occupation: "selfemployed" }]} />  
      }
      {/* Lead modal for student and self-employed */}
      {showLeadModal &&
            <ContactMeContainer data-attribute="contact-me">
                <CloseIcon onClick={() => { setShowLeadModal(false); resetContactFields(); }} />
                <ContactMeContent>
                    <ContactMeHeadline>Du er næsten i mål</ContactMeHeadline>
                    <ContactMeSubtitle>Du har særlige muligheder for dækning. Udfyld formularen, så kontakter vi dig og hjælper dig med at finde den løsning, der passer bedst til din situation.</ContactMeSubtitle>
                    {/* {selectedProduct && (
                        selectedOption?.occupation === "selfemployed" ? (
                            <ContactMeChosenProduct>{selectedProduct.ProductTitle} er desværre ikke tilgængelig for selvstændige. Vil du ringes op om dine muligheder?</ContactMeChosenProduct>
                        ) : selectedOption?.occupation === "student" ? (
                            <ContactMeChosenProduct>{selectedProduct.ProductTitle} er desværre ikke tilgængelig for studerende. Vil du ringes op om dine muligheder?</ContactMeChosenProduct>
                        ) : (
                            <ContactMeChosenProduct>Bliv ringet op om: {selectedProduct.ProductTitle}</ContactMeChosenProduct>
                        )
                    )} */}
                    <ModalInputContainer>
                        <ModalInput id="name" type="text" placeholder="Dit navn" autoComplete="given-name" value={firstName ?? ""}
                            onChange={(e) => {
                                setFirstName(e.currentTarget.value);
                                if (e.currentTarget.value.trim() !== "") {
                                    setShowFirstNameError(false);
                                }
                            }}
                        />
                        {showFirstNameError && (firstName === undefined || firstName.trim() === "") &&
                            <ErrorMessage>Hov, her skal dit navn stå</ErrorMessage>
                        }
                        <ModalInput id="tel" type="tel" placeholder="Dit telefonnummer" minLength={8} autoComplete="tel" value={mobile ?? ""}
                            onChange={(e) => {
                                setMobile(e.currentTarget.value);
                                if (isMobileValid(e.currentTarget.value)) {
                                    setShowMobileError(false);
                                }
                            }}
                        />
                        {showMobileError && !isMobileValid(mobile) && 
                            <ErrorMessage>{!mobile || mobile.trim() === "" ? "Hov, vi kan ikke ringe til dig uden telefonnummer" : "Hov, indtast venligst et gyldigt telefonnummer"}</ErrorMessage>
                        }
                    </ModalInputContainer>
                    <Button className="button" onClick={checkSubmit} active={isContactFormValid}>Ring mig op</Button>
                </ContactMeContent>
            </ContactMeContainer>
        }
    </ProductBannerContainer>
  );
}
